import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[#0A0B0F] px-4">
          <div className="w-full max-w-md text-center">
            <div className="mb-6 font-mono text-6xl text-red-600">!</div>
            <h1 className="mb-2 font-heading text-2xl font-black uppercase tracking-wider text-red-600">
              System Malfunction
            </h1>
            <p className="mb-6 font-mono text-xs text-zinc-500">
              A critical error has occurred. The component tree has been
              compromised.
            </p>

            {this.state.error && (
              <div className="mb-6 rounded border border-red-500/20 bg-red-500/5 p-4 text-left">
                <div className="mb-1 font-mono text-ui-2xs md:text-ui-xs font-bold uppercase tracking-wider text-red-500">
                  Error Details
                </div>
                <p className="break-all font-mono text-ui-xs lg:text-ui-sm text-zinc-400">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleRetry}
                className="flex-1 rounded border-2 border-red-600 bg-red-600/10 py-3 font-heading text-sm font-bold uppercase tracking-wider text-red-500 transition-colors hover:bg-red-600/20"
              >
                Retry
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 rounded border border-zinc-700 py-3 font-heading text-sm font-bold uppercase tracking-wider text-zinc-400 transition-colors hover:border-zinc-500 hover:text-zinc-200"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
